import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AppSettings } from '../../config/app-settings.js';
import Breadcrumb from '../../components/general/breadcrumb';

function DashboardDocumentation() {
  const context = useContext(AppSettings);

  useEffect(() => {
    context.handleSetAppContentClass('');
    context.handleSetAppSidebarTwo(false);
    context.handleSetAppSidebarWide(false);
    context.handleSetAppSidebarNone(true);
    context.handleSetAppTopMenu(true);

    return () => {
      context.handleSetAppSidebarTwo(false);
      context.handleSetAppSidebarNone(false);
      context.handleSetAppSidebarWide(false);
      context.handleSetAppTopMenu(false);
    };
  }, []);

  return (
    <>
      {/* Breadcrumb */}
      <div className="row">
        <div className="col-sm-12">
          <Breadcrumb />
        </div>
      </div>

      {/* Page Header */}
      <div className="row">
        <div className="col-sm-12">
          <h1 className="page-header mb-3">
            <i className="fa fa-chart-simple me-2"></i>
            DASHBOARD PANELS DOCUMENTATION
          </h1>
          <p className="text-gray-600 mb-4">
            Comprehensive guide to using the Congestion Dashboard for traffic analysis and reporting.
          </p>
        </div>
      </div>

      {/* Module Overview */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading bg-primary">
              <h4 className="panel-title">Module Overview</h4>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-md-8">
                  <h5 className="mb-3">Key Features</h5>
                  <ul className="mb-0">
                    <li className="text-black mb-2">
                      View annual, seasonal, and monthly congestion index (CI) metrics
                    </li>
                    <li className="text-black mb-2">
                      Network-wide performance visualization
                    </li>
                    <li className="text-black mb-2">
                      Time period analysis (AM Peak, PM Peak, Mid-Day, Evening)
                    </li>
                    <li className="text-black mb-2">
                      Interactive data tables with historical trends
                    </li>
                    <li className="text-black mb-2">
                      Customizable report generation
                    </li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <div className="bg-gray-900 rounded p-4 text-center">
                    <i className="fa fa-chart-simple fa-4x text-primary mb-3"></i>
                    <Link
                      to="/dashboard-panels/dashboard/"
                      className="btn btn-primary btn-sm w-100"
                    >
                      Open Dashboard <i className="fa fa-arrow-right ms-2"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-play-circle me-2"></i>
                Getting Started
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                Learn how to generate your first traffic report.
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Accessing the Dashboard
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Navigate to Dashboard Panels → Congestion Dashboard from the main menu.
                  </p>
                  <ol className="ms-4 text-black">
                    <li className="mb-1">Click on "Dashboard Panels" in the sidebar menu</li>
                    <li className="mb-1">Select "Congestion Dashboard"</li>
                    <li className="mb-1">The dashboard will load with the report controls visible</li>
                  </ol>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Generating a Report
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Use the sidebar controls to configure your report parameters.
                  </p>
                  <ol className="ms-4 text-black">
                    <li className="mb-1">Select your network (e.g., RDCO Network)</li>
                    <li className="mb-1">Choose the dataset type (Segments or Intersections)</li>
                    <li className="mb-1">Pick the year from available data</li>
                    <li className="mb-1">Select the time period for analysis</li>
                    <li className="mb-1">Click "Generate Report" to view results</li>
                  </ol>
                  <div className="alert alert-info ms-4 mt-2" role="alert">
                    <i className="fa fa-info-circle me-2"></i>
                    <strong>Note:</strong> Reports are generated dynamically and may take a few seconds to load and process, depending on the selected parameters.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Understanding the Dashboard */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-chart-line me-2"></i>
                Understanding the Dashboard
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                Overview of dashboard components and metrics.
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Annual Average CI
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Displays the yearly average congestion index across your selected network and dataset. This provides a high-level view of overall network performance.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Seasonal Average CI
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Shows congestion patterns across different seasons (Spring, Summer, Fall, Winter), helping identify seasonal traffic trends.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Monthly Average CI
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Detailed monthly breakdown presented in an interactive table format. Use this to identify specific months with higher or lower congestion levels.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Congestion Index (CI) Explained
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    The Congestion Index is a normalized metric where values represent a level-of-service congestion score with 1.0 having no congestion. The lower the score, the more congestion a road segment or intersection experienced.  Values are typically displayed as percentages (e.g., 0.75 = 75% congestion score).  A good way to interpret the scores is considering them as a report card score.  Letter grades will be implemented soon, similar to LOS ratings for traffic flow density.
                  </p>
                  <div className="alert alert-info ms-4 mt-2" role="alert">
                    <i className="fa fa-info-circle me-2"></i>
                    <strong>Note:</strong> CI values vary by time period. AM and PM peaks typically show higher congestion than mid-day or evening periods.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Parameters */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-filter me-2"></i>
                Report Parameters
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                Detailed explanation of available filters and options.
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Network Selection
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Choose which traffic network to analyze. Currently supports RDCO Network with potential for additional networks.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Dataset Types
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Select between Segments (roadway sections) or Intersections (junction points) for different analysis perspectives.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Time Period Options
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Available time periods include:
                  </p>
                  <ul className="ms-4 text-black">
                    <li className="mb-1">All Day (24 hours) - Complete daily overview</li>
                    <li className="mb-1">Early Morning (12:00-07:00) - Early Morning traffic</li>
                    <li className="mb-1">AM Peak (07:00-09:00) - Late night and early morning peak period</li>
                    <li className="mb-1">Mid-Day (09:00-16:00) - Mid-day traffic</li>
                    <li className="mb-1">PM Peak (16:00-18:00) - Afternoon peak period</li>
                    <li className="mb-1">Evening (18:00-24:00) - Evening and Night traffic</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tips & Best Practices */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-lightbulb me-2"></i>
                Tips & Best Practices
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                Recommendations for effective dashboard usage.
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Comparative Analysis
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Generate reports for multiple years or time periods to identify trends and changes in traffic patterns over time.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Peak Period Focus
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    For congestion management, focus on AM Peak and PM Peak periods as these typically show the highest congestion levels.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Data Availability
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    The year dropdown only shows years with available data. If a year is missing, data does not exist or is yet to be collected/processed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="row">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading bg-gray-900">
              <h4 className="panel-title">
                <i className="fa fa-life-ring me-2"></i>
                Need Help?
              </h4>
            </div>
            <div className="panel-body">
              <div className="row text-center">
                <div className="col-md-12 mb-3">
                  <i className="fa fa-envelope fa-2x text-primary mb-2"></i>
                  <h5 className="text-black-900 mb-2">Contact Support</h5>
                  <p className="text-black fs-13px mb-2">
                    Email us for technical assistance
                  </p>
                  <a href="mailto:support@acuere.com" className="text-primary">
                    support@acuere.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DashboardDocumentation;